import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
interface LegalModalProps {
  document: LegalDocument | null;
  onClose: () => void;
}
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    const previous = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      window.document.body.style.overflow = previous;
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-197" data-mp="1huooor6hkxs7">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-198" data-mp="1cuah1a8srpup">
          <motion.div className="absolute inset-0 bg-bark/50" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={onClose} data-id="element-199" data-mp="1ar83c9yzst7m" />
          <motion.div role="dialog" aria-modal="true" aria-label={doc.title} className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-cream shadow-lift sm:max-h-[86vh] sm:rounded-2xl" initial={{
        opacity: 0,
        scale: 0.97,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-200" data-mp="1pnpdixvg41sl">
            <header className="flex items-start justify-between gap-6 border-b border-bark/10 bg-sand/60 px-6 py-5 sm:px-8" data-id="element-201" data-mp="99z9ts88nfjh">
              <div data-id="element-202" data-mp="e7953oib6lf8">
                <h2 className="font-display text-xl text-bark sm:text-2xl" data-id="element-203" data-mp="e6pocb4ea32s">{doc.title}</h2>
                <p className="mt-1 text-xs text-ink/60" data-id="element-204" data-mp="669whvslfufr">{doc.updated}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="-mr-1 rounded-full p-2 text-ink/70 transition-colors duration-150 ease-out hover:bg-bark/5 hover:text-bark focus:outline-none focus-visible:ring-2 focus-visible:ring-clay" data-id="element-205" data-mp="17zw96s0i3na">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-206" data-mp="3jdl323netm30" />
              </button>
            </header>

            <div className="overflow-y-auto px-6 py-6 sm:px-8 sm:py-8" data-id="element-207" data-mp="3egcumus2cwey">
              <div className="mx-auto max-w-2xl space-y-7" data-id="element-208" data-mp="3tcano46oxkmy">
                {doc.sections.map(section => <section key={section.heading} data-id="element-209" data-mp="3odh7g400rfj3">
                    <h3 className="font-display text-base text-bark sm:text-lg" data-id="element-210" data-mp="3oco4le46la68">{section.heading}</h3>
                    <div className="mt-2 space-y-3" data-id="element-211" data-mp="18krya0s96ugz">
                      {section.body.map((paragraph, i) => <p key={i} className="text-sm leading-relaxed text-ink/85" data-id="element-212" data-mp="13mgo1oy1qhy8" data-mpi={i}>
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}